import React from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Business as WorkspaceIcon,
  Check as CheckIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Workspace {
  id: string;
  name: string;
  is_home: boolean;
}

/**
 * Which company you are working in, and how to change it.
 *
 * Lives in the top bar rather than Settings because switching changes every screen — posts,
 * calendar, history, analytics, connections — and the failure it exists to prevent is
 * publishing one company's post from another's account. That has to be visible while you work,
 * not a preference you set once and forget.
 *
 * Hidden entirely for anyone in a single workspace, which is everyone until a second one is
 * created: a switcher with nothing to switch to is just noise in the chrome.
 */
export const WorkspaceSwitcher: React.FC = () => {
  const { token } = useAuth();
  const [workspaces, setWorkspaces] = React.useState<Workspace[]>([]);
  const [active, setActive] = React.useState<string | null>(null);
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const [switching, setSwitching] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => fetch(`${API_URL}/api/workspaces`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        setWorkspaces(data.workspaces || []);
        setActive(data.active || null);
      })
      // The app works fine without the switcher; a failure here must not break the chrome
      .catch(() => undefined);
    load();
    // Loaded once now that the frame persists across pages; Misc Config announces changes
    window.addEventListener('shoutvector:workspaces-changed', load);
    return () => {
      cancelled = true;
      window.removeEventListener('shoutvector:workspaces-changed', load);
    };
  }, [token]);

  const current = workspaces.find(w => w.id === active);

  const switchTo = async (id: string) => {
    if (id === active) {
      setAnchor(null);
      return;
    }
    setSwitching(true);
    try {
      const res = await fetch(`${API_URL}/api/workspaces/switch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId: id }),
      });
      if (!res.ok) {
        setSwitching(false);
        return;
      }
      // A full reload rather than a state update. Every page holds its own fetched data, and a
      // partial refresh would leave one company's posts on screen beside another's connections
      // — which is precisely the confusion this feature has to avoid.
      window.location.reload();
    } catch {
      setSwitching(false);
    }
  };

  // Nothing to switch between
  if (workspaces.length < 2) return null;

  return (
    <>
      <Button
        color="inherit"
        onClick={e => setAnchor(e.currentTarget)}
        disabled={switching}
        startIcon={switching ? <CircularProgress size={16} color="inherit" /> : <WorkspaceIcon />}
        endIcon={<ExpandIcon />}
        sx={{
          textTransform: 'none',
          mr: 1,
          // Outlined rather than plain text: this is the app's current context, not another
          // action sitting in the bar.
          border: theme => `1px solid ${theme.palette.primary.contrastText}40`,
          borderRadius: 1,
          px: 1.5,
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, fontWeight: 600 }}>
          {current?.name || 'Workspace'}
        </Box>
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
          Working in
        </Typography>
        <Divider />
        {workspaces.map(w => (
          <MenuItem key={w.id} selected={w.id === active} onClick={() => switchTo(w.id)}>
            <ListItemIcon>{w.id === active ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText
              primary={w.name}
              secondary={w.is_home ? 'Your default' : undefined}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
